import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  FINANCIAL_YEAR,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { deletePurchase, getAllPurchase } from "./services/purchase.services";
import {
  clearFilterCustomPurchase,
  clearPurchaseID,
  setFilterCustomPurchase,
  setPurchaseID,
} from "./purchaseSlice";
import { clearItemList } from "../Item/itemSlice";
import { TextBox } from "@progress/kendo-react-inputs";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import _debounce from "lodash/debounce";

const Purchase: React.FC = () => {
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  const handleCreate = () => {
    navigate("/purchase/create");
  };

  return (
    <>
      <DeletePurchaseDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Purchase List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Add New"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <PurchaseGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const PurchaseGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const isEditBtnRef = useRef<any>(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const PurchaseList = useAppSelector((state) => state.purchase.PurchaseList);
  const loading = useAppSelector((state) => state.purchase.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterCustomPurchase = useAppSelector(
    (state) => state.purchase.FilterCustomPurchase
  );

  useEffect(() => {
    return () => {
      dispatch(clearItemList());
      dispatch(setCurrentPage(0));
      if (!isEditBtnRef.current) {
        dispatch(clearFilterCustomPurchase());
      }
    };
  }, []);

  useEffect(() => {
    const payload = {
      ...FilterCustomPurchase,
      financial_year: FINANCIAL_YEAR,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllPurchase(payload));
  }, [currentPage, pageLimit]);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (purchase_guid: any) => {
      navigate("/purchase/edit", {
        state: { purchase_guid: purchase_guid },
      });
    };

    const handleOpenDeleteDialog = (purchase_guid: any) => {
      dispatch(openDialog("deletePurchase"));
      dispatch(setPurchaseID(purchase_guid));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            ref={isEditBtnRef}
            onClick={() => {
              isEditBtnRef.current = true;
              handleEdit(props.dataItem?.purchase_guid);
            }}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem?.purchase_guid)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 9 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        className="responsive-table"
        filterable={true}
        filter={filter}
        data={filter ? filterBy(PurchaseList, filter) : PurchaseList}
        onFilterChange={handleFilterChange}
        total={PurchaseList[0]?.itemcount || PurchaseList?.length}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="purchase_no_string"
          title="Purchase No"
          filterable={true}
          width={150}
          cell={(props: any) => (
            <td>
              <div
                className={isEditAccess ? "hoverableName" : "normalName"}
                onClick={() =>
                  isEditAccess &&
                  navigate("/purchase/edit", {
                    state: {
                      purchase_guid: props.dataItem?.purchase_guid,
                    },
                  })
                }
              >
                {props.dataItem?.purchase_no_string}
              </div>
            </td>
          )}
        />
        <Column
          field="invoice_no"
          title="Invoice No"
          width={150}
          cell={(props: any) => (
            <td>
              <div
                className={isEditAccess ? "hoverableName" : "normalName"}
                onClick={() =>
                  isEditAccess &&
                  navigate("/purchase/edit", {
                    state: {
                      purchase_guid: props.dataItem?.purchase_guid,
                    },
                  })
                }
              >
                {props.dataItem?.invoice_no}
              </div>
            </td>
          )}
        />
        <Column field="invoice_date" title="Invoice Date" width={150} />
        <Column
          field="account_name"
          title="Account Name"
          width={250}
          filterCell={CommonFilterCell}
        />
        <Column field="gst_no" title="GST No" width={150} />
        <Column field="due_date" title="Due Date" width={150} />
        <Column
          field="taxable_amount"
          title="Taxable Amount"
          width={150}
          cell={(props: any) => (
            <td className="text-end">
              {props.dataItem?.taxable_amount
                ? formatIndianNumber(props.dataItem?.taxable_amount)
                : null}
            </td>
          )}
        />
        <Column
          field="gst"
          title="Total GST"
          width={150}
          cell={(props: any) => (
            <td className="text-end">
              {props.dataItem?.gst
                ? formatIndianNumber(props.dataItem?.gst)
                : null}
            </td>
          )}
        />
        <Column
          field="total_amount"
          title="Grand Amount"
          width={150}
          cell={(props: any) => (
            <td className="text-end">
              {props.dataItem?.total_amount
                ? formatIndianNumber(props.dataItem?.total_amount)
                : null}
            </td>
          )}
        />
        {(isEditAccess || isDeleteAccess) && (
          <Column
            field="id"
            title="Actions"
            cell={MyEditCommandCell}
            width={"110px"}
            locked={true}
            filterable={false}
          />
        )}
      </KendoGrid>
    </>
  );
};

const DeletePurchaseDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const PurchaseID = useAppSelector((state) => state.purchase.PurchaseID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterCustomPurchase = useAppSelector(
    (state) => state.purchase.FilterCustomPurchase
  );

  const handleDeleteAction = async (ID: any) => {
    if (ID) {
      const response = await dispatch(deletePurchase(ID));
      dispatch(setCurrentPage(0));
      const payload = {
        ...FilterCustomPurchase,
        financial_year: FINANCIAL_YEAR,
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      if (response.payload.Data.Status === 200) {
        dispatch(getAllPurchase(payload));
        dispatch(closeDialog());
        dispatch(clearPurchaseID());
      } else {
        dispatch(getAllPurchase(payload));
        dispatch(closeDialog());
        dispatch(clearPurchaseID());
      }
    }
  };

  return (
    <>
      {dialogName === "deletePurchase" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Purchase"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Purchase?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(PurchaseID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

const CommonFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterCustomPurchase: any = useAppSelector(
    (state) => state.purchase.FilterCustomPurchase
  );
  const isFilterRef = useRef(true);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [inputField, setInputField] = useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        financial_year: FINANCIAL_YEAR,
        limit: +pageLimit,
        skip: 0 * +pageLimit,
      };

      dispatch(getAllPurchase(payload));
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...FilterCustomPurchase,
        [inputField]: inputValue,
      };
      dispatch(setFilterCustomPurchase(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = { ...FilterCustomPurchase, [props.field]: "" };
    dispatch(setFilterCustomPurchase(updatedFilters));

    const payload = {
      ...updatedFilters,
      financial_year: FINANCIAL_YEAR,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };

    dispatch(getAllPurchase(payload));
  };

  return (
    <div className="d-flex">
      <TextBox
        value={FilterCustomPurchase[props.field] || ""}
        name={props.field || ""}
        onChange={onChange}
      />
      <Button
        svgIcon={filterClearIcon}
        title="Clear"
        disabled={!FilterCustomPurchase[props.field]}
        onClick={onClearButtonClick}
        type="button"
        style={{
          marginLeft: "8px",
        }}
      ></Button>
    </div>
  );
};

export default Purchase;
